export const last = (arr?: any[]) =>
  Array.isArray(arr) ? arr[arr.length - 1] : undefined;

export const union = <T>(...args: T[][]) => [...new Set(args.flat()).values()];

/**
 * The reason spread operator is not used here is because of its behavior when passing an entry with undefined value explicitly:
 * const a = { hello: 'world' }
 * const b = {...a, hello: undefined }; // {hello: undefined}
 * We want to ignore the undefined value and use the defined value from the previous object instead.
 */
export const defaults = <T extends Record<string, any>>(
  ...args: (T | undefined)[]
) =>
  args.reduce<T>(
    (res, obj) =>
      Object.entries(obj ?? {}).reduce((_res, [key, value]) => {
        if (typeof _res[key] === 'undefined' && typeof value !== 'undefined') {
          (_res as Record<string, any>)[key] = value;
        }

        return _res;
      }, res),
    {} as T,
  );

export const deepDefaults = <T extends Record<string, any>>(
  dest?: T,
  ...rest: (T | undefined)[]
): T | undefined => {
  const keys = union<keyof T>(...rest.map((value) => Object.keys(value ?? {})));
  const all = [dest, ...rest];

  return keys.reduce<T | undefined>(
    (res, key) => {
      const values = all.map((value) => value?.[key]);
      const merged = defaults(...(values as [T[keyof T]]));

      const hasValues =
        Object.values(merged).filter((value) => typeof value !== 'undefined')
          .length > 0;

      if (hasValues) {
        res = res ?? ({} as T);
        res[key] = merged;
      } else {
        delete res?.[key];
      }

      return res;
    },
    dest ? { ...dest } : dest,
  );
};
